/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, computed, action } from "mobx";
import { loadStatus, responseSource, pageUsageStatus } from "constants/enums";
import { DEFAULT_MIN_ZOOM, DEFAULT_MAX_ZOOM, TILE_OVERLAP_HEIGHT } from "constants/constants";
import { getZoomLevelDimension } from "utils/PageUtils";
import { getPageZoomLevel } from "selectors/zoom";
import { PageTile } from "./PageTile";
import * as stores from "stores";
import { AcroformAppearance } from "stores/AcroformAppearance";
import { isFullScreenMode } from "selectors/documentSelectors";
import { getCurrentPageIndex } from "selectors/pageNumber";

const NUM_TILES = 4;
class Page {
  @observable pageBitmap = "";
  @observable pageLoadStatus = loadStatus.NOT_LOADED;
  @observable origHeight = 0;
  @observable origWidth = 0;
  @observable thumbnailBitmap = "";
  @observable thumbnailLoadStatus = loadStatus.NOT_LOADED;
  @observable thumbnailWidth = 0;
  @observable thumbnailHeight = 0;
  @observable thumbnailBitmapSize = 0;
  @observable pageAccText = undefined;
  @observable paraAccText = undefined;
  @observable thumbnailLoadedFrom = responseSource.NONE;
  @observable pageTiles = [];
  @observable useTiles = false;
  @observable isMounted = false;
  @observable minZoom = 0;
  @observable maxZoom = 0;
  @observable shouldTriggerEnterEvent = true;
  accessiblePageContentHandle = false;
  pageBitmapSize = 0;
  fontList;
  @observable boxes = {
    cropBox: [],
    mediaBox: []
  };
  @observable hyperlinkRects = [];
  @observable pageTextLoadStatus = loadStatus.NOT_LOADED;
  hyperlinkLoadStatus = loadStatus.NOT_LOADED;
  hyperlinkLoadPriority = Number.MAX_SAFE_INTEGER;
  pageTextLoadPriority = Number.MAX_SAFE_INTEGER;
  actions = null;
  pageUsageStatus = pageUsageStatus.NOT_USED;
  pageId = -1;

  constructor({
    origHeight = 0,
    origWidth = 0,
    index = 0,
    minZoom = DEFAULT_MIN_ZOOM,
    maxZoom = DEFAULT_MAX_ZOOM,
    initialStatus = loadStatus.NOT_LOADED,
    tMatrix = {},
    boxes = { cropBox: [], mediaBox: [] },
    id = -1
  }) {
    this.origHeight = origHeight;
    this.origWidth = origWidth;
    this.index = index;
    this.pageLoadStatus = initialStatus;
    this.tMatrix = tMatrix;
    this.elementHandle = null;
    this.minZoom = minZoom;
    this.maxZoom = maxZoom;
    this.boxes = boxes;
    this.id = `${id}`;

    const pageTiles = [];
    for (let i = 0; i < NUM_TILES; i++) {
      pageTiles.push(new PageTile());
    }
    this.pageTiles.replace(pageTiles);
    this.acroformAppearancesMap = new Map();
  }

  isValidTileIndex(tileIndex) {
    return tileIndex >= 0 && tileIndex < this.pageTiles.length;
  }

  @action.bound setShouldTriggerEnterEvent(status) {
    this.shouldTriggerEnterEvent = status;
    if (!this.shouldTriggerEnterEvent) {
      // enter event has been triggered for this page index. Trigger leave event for pages for whom enter has been triggered but leave has not been triggered
      stores.documentStore.triggerMouseLeaveEvent(this.index);
    }
  }

  @action.bound
  setActionsForPage(actions) {
    if (!this.actions && actions) {
      this.actions = actions;
    }
  }

  @computed
  get haveAllTilesLoaded() {
    return this.useTiles && this.pageTiles.every(tile => tile.isLoaded);
  }

  @computed
  get currPageHeight() {
    let zoomlevel;
    if (isFullScreenMode(stores) && this.index !== getCurrentPageIndex(stores)) {
      zoomlevel = stores.zoom.getFitPageZoomLevel(this.index);
    } else {
      zoomlevel = getPageZoomLevel(stores, this.index);
    }
    return getZoomLevelDimension(this.origHeight, zoomlevel);
  }

  @computed
  get currPageWidth() {
    let zoomlevel;
    if (isFullScreenMode(stores) && this.index !== getCurrentPageIndex(stores)) {
      zoomlevel = stores.zoom.getFitPageZoomLevel(this.index);
    } else {
      zoomlevel = getPageZoomLevel(stores, this.index);
    }
    return getZoomLevelDimension(this.origWidth, zoomlevel);
  }

  @computed
  get bitmapSize() {
    if (this.pageLoadStatus === loadStatus.LOADED) return this.currPageHeight * this.currPageWidth;
    return 0;
  }

  @computed
  get tileCoords() {
    let tiles = [];
    const pageWidth = this.currPageWidth;
    const pageHeight = this.currPageHeight;
    const tileHeight = Math.floor(pageHeight / NUM_TILES);

    for (let i = 0; i < NUM_TILES; i++) {
      tiles.push({
        startX: 0,
        startY: tileHeight * i,
        endX: pageWidth,
        /*
          Whenever we place two images side by side without any overlap, there exists some empty space at the boundary.
          To fix this, we have extended the height of the first three tiles.
          The visible portion of tile is still same while the extra portion covers the empty space and rest is covered by the tile below.
          
          Further details can be found at the JIRA ID: https://jira.corp.adobe.com/browse/ADCS-19861
        */
        endY: i === NUM_TILES - 1 ? pageHeight : tileHeight * (i + 1) + TILE_OVERLAP_HEIGHT
      });
    }

    return tiles;
  }

  setAccessiblePageContentHandle(handle) {
    if (handle) {
      this.accessiblePageContentHandle = handle;
    }
  }

  getAppearanceDataForField(fieldId) {
    let appearanceObject = this.acroformAppearancesMap.get(fieldId);
    if (!appearanceObject) {
      appearanceObject = new AcroformAppearance(fieldId);
      this.acroformAppearancesMap.set(fieldId, appearanceObject);
    }
    return appearanceObject;
  }

  // PDF coordinates fetched from T5 are getting stored
  @action.bound setHyperlinkRects(rects) {
    this.hyperlinkRects = rects;
  }
}

export { Page };
